import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Sparkles, Upload, FileText, Check } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ParsedJD {
  title: string;
  responsibilities: string[];
  requirements: string[];
  skills: string[];
  qualifications: string[];
  benefits: string[];
}

export default function CreateRole() {
  const [, setLocation] = useLocation();
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [jdText, setJdText] = useState("");
  const [parsedJD, setParsedJD] = useState<ParsedJD | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    requirements: [""],
    mustHaveSkills: [""],
    dealBreakers: [""],
    contextualExpectations: "",
  });

  const handleParseJD = async () => {
    if (!jdText.trim()) {
      toast.error("Please paste a job description first");
      return;
    }

    setParsing(true);
    try {
      const res = await apiRequest("POST", "/api/roles/parse-jd", { documentText: jdText });

      if (!res.ok) throw new Error("Failed to parse");

      const result = await res.json();
      setParsedJD(result.parsedData);
      
      setFormData({
        title: result.suggestedRole.title,
        description: result.suggestedRole.description,
        requirements: result.suggestedRole.requirements.length > 0 ? result.suggestedRole.requirements : [""],
        mustHaveSkills: result.suggestedRole.mustHaveSkills.length > 0 ? result.suggestedRole.mustHaveSkills : [""],
        dealBreakers: result.suggestedRole.dealBreakers.length > 0 ? result.suggestedRole.dealBreakers : [""],
        contextualExpectations: result.suggestedRole.contextualExpectations,
      });

      toast.success("Job description parsed! Review and customize below.");
    } catch (error) {
      toast.error("Failed to parse job description");
    } finally {
      setParsing(false);
    }
  };

  const handleCreateFromJD = async () => {
    setLoading(true);
    try {
      const res = await apiRequest("POST", "/api/roles/create-from-jd", {
          documentText: jdText,
          overrides: {
            ...formData,
            requirements: formData.requirements.filter(r => r.trim()),
            mustHaveSkills: formData.mustHaveSkills.filter(s => s.trim()),
            dealBreakers: formData.dealBreakers.filter(d => d.trim()),
          },
        });

      if (!res.ok) throw new Error("Failed to create role");

      const result = await res.json();
      toast.success(`Role created with ${result.questionsGenerated} AI-generated questions!`);
      setLocation("/agent/dashboard");
    } catch (error) {
      toast.error("Failed to create role");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        requirements: formData.requirements.filter(r => r.trim()),
        mustHaveSkills: formData.mustHaveSkills.filter(s => s.trim()),
        dealBreakers: formData.dealBreakers.filter(d => d.trim()),
      };

      const res = await apiRequest("POST", "/api/roles", payload);

      if (!res.ok) {
        throw new Error("Failed to create role");
      }

      const result = await res.json();
      toast.success(`Role created with ${result.questionsGenerated} AI-generated questions!`);
      setLocation("/agent/dashboard");
    } catch (error) {
      toast.error("Failed to create role");
    } finally {
      setLoading(false);
    }
  };

  const addItem = (field: keyof typeof formData) => {
    if (Array.isArray(formData[field])) {
      setFormData({
        ...formData,
        [field]: [...(formData[field] as string[]), ""],
      });
    }
  };

  const removeItem = (field: keyof typeof formData, index: number) => {
    if (Array.isArray(formData[field])) {
      const updated = [...(formData[field] as string[])];
      updated.splice(index, 1);
      setFormData({ ...formData, [field]: updated });
    }
  };

  const updateItem = (field: keyof typeof formData, index: number, value: string) => {
    if (Array.isArray(formData[field])) {
      const updated = [...(formData[field] as string[])];
      updated[index] = value;
      setFormData({ ...formData, [field]: updated });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <Link href="/agent/dashboard">
            <Button variant="ghost" size="sm" data-testid="button-back">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-5xl font-bold mb-4">Create New Role</h1>
          <p className="text-xl text-muted-foreground">
            Upload a job description or enter details manually
          </p>
        </div>

        <Tabs defaultValue="upload" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload" data-testid="tab-upload">
              <Upload className="w-4 h-4 mr-2" />
              Upload JD
            </TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-manual">
              <FileText className="w-4 h-4 mr-2" />
              Enter Manually
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-6">
            <Card className="p-6 cyber-border">
              <h2 className="text-2xl font-bold mb-4">Paste Job Description</h2>
              <p className="text-muted-foreground mb-4">
                Paste your job description document text and our AI will extract role details and generate custom interview questions.
              </p>
              
              <Textarea
                data-testid="textarea-jd"
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
                placeholder="Paste your full job description here..."
                className="min-h-[250px] mb-4"
              />

              <Button
                onClick={handleParseJD}
                disabled={parsing || !jdText.trim()}
                className="w-full"
                variant="outline"
                data-testid="button-parse-jd"
              >
                {parsing ? "Analyzing..." : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Parse & Extract Role Details
                  </>
                )}
              </Button>
            </Card>

            {parsedJD && (
              <>
                <Card className="p-6 bg-primary/5 border-primary/30">
                  <div className="flex items-center gap-2 mb-4">
                    <Check className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-bold">AI Extracted Data</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-semibold text-muted-foreground">Title</p>
                      <p>{parsedJD.title}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-muted-foreground">Skills Found</p>
                      <p>{parsedJD.skills.join(", ") || "None extracted"}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-muted-foreground">Requirements</p>
                      <p>{parsedJD.requirements.length} items</p>
                    </div>
                    <div>
                      <p className="font-semibold text-muted-foreground">Qualifications</p>
                      <p>{parsedJD.qualifications.join(", ") || "None extracted"}</p>
                    </div>
                  </div>
                </Card>

                <RoleForm
                  formData={formData}
                  setFormData={setFormData}
                  addItem={addItem}
                  removeItem={removeItem}
                  updateItem={updateItem}
                />

                <Button
                  onClick={handleCreateFromJD}
                  disabled={loading}
                  className="w-full cyber-glow text-lg py-6"
                  data-testid="button-create-from-jd"
                >
                  {loading ? "Creating Role..." : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Create Role & Generate Interview Questions
                    </>
                  )}
                </Button>
              </>
            )}
          </TabsContent>

          <TabsContent value="manual">
            <form onSubmit={handleSubmit} className="space-y-6">
              <RoleForm
                formData={formData}
                setFormData={setFormData}
                addItem={addItem}
                removeItem={removeItem}
                updateItem={updateItem}
              />

              <Button
                type="submit"
                className="w-full cyber-glow text-lg py-6"
                disabled={loading}
                data-testid="button-create-role"
              >
                {loading ? "Creating..." : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Create Role & Generate Interview Questions
                  </>
                )}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function RoleForm({
  formData,
  setFormData,
  addItem,
  removeItem,
  updateItem,
}: {
  formData: any;
  setFormData: (data: any) => void;
  addItem: (field: any) => void;
  removeItem: (field: any, index: number) => void;
  updateItem: (field: any, index: number, value: string) => void;
}) {
  return (
    <>
      <Card className="p-6 cyber-border">
        <h2 className="text-2xl font-bold mb-4">Basic Information</h2>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Role Title *</Label>
            <Input
              id="title"
              data-testid="input-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="mt-1.5"
              placeholder="e.g., Senior Software Engineer"
            />
          </div>

          <div>
            <Label htmlFor="description">Role Description *</Label>
            <Textarea
              id="description"
              data-testid="textarea-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              className="mt-1.5 min-h-[120px]"
              placeholder="Describe the role, responsibilities, and team context..."
            />
          </div>
        </div>
      </Card>

      <Card className="p-6 cyber-border">
        <h2 className="text-2xl font-bold mb-4">Requirements</h2>
        
        <div className="space-y-4">
          {formData.requirements.map((req: string, index: number) => (
            <div key={index} className="flex gap-2">
              <Input
                data-testid={`input-requirement-${index}`}
                value={req}
                onChange={(e) => updateItem("requirements", index, e.target.value)}
                placeholder="e.g., 5+ years of React experience"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeItem("requirements", index)}
                data-testid={`button-remove-requirement-${index}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addItem("requirements")}
            data-testid="button-add-requirement"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Requirement
          </Button>
        </div>
      </Card>

      <Card className="p-6 cyber-border">
        <h2 className="text-2xl font-bold mb-4">Must-Have Skills</h2>
        
        <div className="space-y-4">
          {formData.mustHaveSkills.map((skill: string, index: number) => (
            <div key={index} className="flex gap-2">
              <Input
                data-testid={`input-skill-${index}`}
                value={skill}
                onChange={(e) => updateItem("mustHaveSkills", index, e.target.value)}
                placeholder="e.g., TypeScript, Node.js"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeItem("mustHaveSkills", index)}
                data-testid={`button-remove-skill-${index}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addItem("mustHaveSkills")}
            data-testid="button-add-skill"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Skill
          </Button>
        </div>
      </Card>

      <Card className="p-6 cyber-border">
        <h2 className="text-2xl font-bold mb-4">Deal-Breakers</h2>
        
        <div className="space-y-4">
          {formData.dealBreakers.map((breaker: string, index: number) => (
            <div key={index} className="flex gap-2">
              <Input
                data-testid={`input-dealbreaker-${index}`}
                value={breaker}
                onChange={(e) => updateItem("dealBreakers", index, e.target.value)}
                placeholder="e.g., Unable to work remotely"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeItem("dealBreakers", index)}
                data-testid={`button-remove-dealbreaker-${index}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addItem("dealBreakers")}
            data-testid="button-add-dealbreaker"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Deal-Breaker
          </Button>
        </div>
      </Card>

      <Card className="p-6 cyber-border">
        <h2 className="text-2xl font-bold mb-4">Context (Optional)</h2>
        <Textarea
          data-testid="textarea-context"
          value={formData.contextualExpectations}
          onChange={(e) => setFormData({ ...formData, contextualExpectations: e.target.value })}
          className="mt-1.5 min-h-[100px]"
          placeholder="e.g., Customer-facing, safety-critical, fast-paced startup environment..."
        />
      </Card>
    </>
  );
}
